import React, { useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import { SvgIcon, useMediaQuery, useTheme } from '@mui/material'
import { ReactComponent as CloseIcon } from '../../assets/images/Close.svg'
import { useTPPChangeLogModalContext } from './TPPChangeLogModalProvider'
import ChangeLogContent from './components/ChangeLogContent'
import VersionHistoryContent from './components/VersionHistoryContent'
import { GroupedChange } from './types'
import Tabs from '../../shared/components/Tabs/Tabs'

interface TPPChangeLogModalProps {
    mockData?: GroupedChange[]
}

const TPPChangeLogModal = ({ mockData }: TPPChangeLogModalProps) => {
    const { isOpen, tppInfo, selectedColumnId, handleClose, handleColumnChange } = useTPPChangeLogModalContext()
    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

    const [mainTabId, setMainTabId] = useState('changelog')

    if (!tppInfo) return null

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            fullWidth
            fullScreen={isSmallScreen}
            maxWidth="md"
            aria-labelledby="changelog-dialog-title"
            PaperProps={{
                sx: {
                    py: { xs: 2, sm: 2, md: 3 },
                    px: { xs: 2, sm: 3, md: 4 },
                    borderRadius: { xs: 0, sm: '24px' },
                    boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.30)',
                    height: { xs: '100%', sm: '85vh', md: '80vh' },
                    maxHeight: { xs: 'none', sm: '800px' },
                },
            }}
        >
            <DialogTitle id="changelog-dialog-title" sx={{ p: 0, pb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="22x600" color="figma.grayscale.textHier1">
                        Change Log & Version History
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            p: 0.5,
                            color: 'figma.grayscale.textHier2',
                        }}
                    >
                        <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" color="inherit" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Tabs
                    orientation="horizontal"
                    selectedTabId={mainTabId}
                    onTabChange={setMainTabId}
                    tabListSx={{
                        width: 'fit-content',
                        '&::before': { display: 'none' },
                    }}
                    tabPanelSx={{ minHeight: 0 }}
                    tabs={[
                        {
                            id: 'changelog',
                            label: 'Change Log',
                            view: (
                                <Stack flex={1} minHeight={0}>
                                    <Tabs
                                        orientation="horizontal"
                                        selectedTabId={selectedColumnId ?? tppInfo.columns[0]?._id ?? ''}
                                        onTabChange={(id) => handleColumnChange(id)}
                                        sx={{ minHeight: 0 }}
                                        tabListSx={{
                                            mt: 1,
                                            ml: -1.5,
                                            '&::before': { display: 'none' },
                                            '& .MuiTabs-scrollButtons.Mui-disabled': {
                                                width: 0,
                                                minWidth: 0,
                                                opacity: 0,
                                            },
                                        }}
                                        tabPanelSx={{ minHeight: 0 }}
                                        tabs={tppInfo.columns.map((col) => ({
                                            id: col._id,
                                            label: col.title,
                                            view: (
                                                <ChangeLogContent
                                                    tppId={tppInfo.tppId}
                                                    columnId={col._id}
                                                    mockData={mockData}
                                                />
                                            ),
                                        }))}
                                    />
                                </Stack>
                            ),
                        },
                        {
                            id: 'version-history',
                            label: 'Version History',
                            view: (
                                <VersionHistoryContent
                                    tppInfo={tppInfo}
                                    selectedColumnId={selectedColumnId}
                                    onColumnChange={handleColumnChange}
                                />
                            ),
                        },
                    ]}
                />
            </DialogContent>
        </Dialog>
    )
}

export default TPPChangeLogModal
